// Generate steps for algorithm visualization
export const generatePathSteps = (result, locations) => {
  if (!result || !result.path || !result.path.length || !locations.length) {
    return [];
  }

  const steps = [];
  const path = result.path;
  const pathLength = path.length;
  
  // Create a lookup map for locations
  const locationsMap = {};
  locations.forEach(loc => {
    locationsMap[loc.id] = loc;
  });
  
  // Helper function to get location name
  const getLocationName = (id) => {
    const loc = locationsMap[id];
    return loc ? loc.name : `Point ${id}`;
  };
  
  // Helper function to calculate distance between two points
  const calculateDistance = (point1, point2) => {
    if (!point1 || !point2) return 0;
    
    const R = 6371; // Earth's radius in km
    const dLat = (point2.latitude - point1.latitude) * (Math.PI / 180);
    const dLon = (point2.longitude - point1.longitude) * (Math.PI / 180);
    
    const a = 
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(point1.latitude * (Math.PI / 180)) * Math.cos(point2.latitude * (Math.PI / 180)) * 
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  };
  
  // Initial steps
  steps.push({
    step: 0,
    description: `Starting from ${getLocationName(path[0])}.`,
    pathIds: [path[0]],
    currentPath: [getLocationName(path[0])],
    currentPathIndex: 0,
    totalDistanceSoFar: 0,
    distancesList: locations
      .filter(loc => loc.id !== path[0])
      .map(loc => ({
        pointName: loc.name,
        distance: calculateDistance(locationsMap[path[0]], loc),
        visited: false,
        isNext: loc.id === path[1]
      }))
  });
  
  // Generate steps for each path segment
  let totalDistance = 0;
  
  for (let i = 0; i < pathLength - 1; i++) {
    const currentId = path[i];
    const nextId = path[i + 1];
    const currentLoc = locationsMap[currentId];
    const nextLoc = locationsMap[nextId];
    
    if (currentLoc && nextLoc) {
      const segmentDistance = calculateDistance(currentLoc, nextLoc);
      totalDistance += segmentDistance;
      
      // Build current path names for display
      const currentPathNames = path.slice(0, i + 2).map(id => getLocationName(id));
      
      // Create step data
      steps.push({
        step: i + 1,
        description: `Moving from ${getLocationName(currentId)} to ${getLocationName(nextId)}, distance: ${segmentDistance.toFixed(2)}km.`,
        pathIds: path.slice(0, i + 2),
        currentPath: currentPathNames,
        currentPathIndex: i + 1,
        totalDistanceSoFar: totalDistance,
        distancesList: locations
          .filter(loc => !path.slice(0, i + 1).includes(loc.id))
          .map(loc => ({
            pointName: loc.name,
            distance: calculateDistance(currentLoc, loc),
            visited: path.slice(0, i + 2).includes(loc.id),
            isNext: loc.id === (i < pathLength - 2 ? path[i + 2] : null)
          }))
      });
    }
  }
  
  // Final step (if path is a loop and returns to start)
  if (pathLength > 2 && path[0] === path[pathLength - 1]) {
    steps.push({
      step: pathLength - 1,
      description: `Returned to starting point ${getLocationName(path[0])}, total trip distance: ${totalDistance.toFixed(2)}km.`,
      pathIds: path,
      currentPath: path.map(id => getLocationName(id)),
      currentPathIndex: pathLength - 1,
      totalDistanceSoFar: totalDistance,
      distancesList: []
    });
  }
  
  return steps;
}; 